/**
 * Pre-fill settings — Settings > My-School (v4; cascading district → campus).
 * Shown only to campuses with at least one by-component order column.
 *
 * ## v5: there is no off switch (Sheila, 2026-08-26)
 *
 * *"I specifically said OVS needs to remove the toggle to disable splits. That
 * shouldn't be possible if it's OVS, they just need to update to 100%"*
 *
 * This card renders for OVS campuses only, so the split is ALWAYS ON here and
 * the on/off `Switch` is gone: a school that wants complete meals says so with
 * the percentages — 100% per component — which is the same number the resolver
 * reads. A second switch beside those numbers is what caused the incident
 * below, and the only fix that removes the class of bug is removing the switch.
 *
 * The percentage editor therefore renders UNCONDITIONALLY. A campus whose
 * database row still says `DISABLED` (written before the switch was removed)
 * gets `SplitStateNotice`'s legacy banner — an explanation plus a one-way
 * "clear the old opt-out" button — and the same editable per-mealtime cards.
 * Nothing about resolution changed: a stored `DISABLED` still ships 100% until
 * it is cleared, because turning campuses on behind the school's back would
 * ship food nobody ordered.
 *
 * ## What v4 fixed (still true)
 *
 * A campus with the split OFF used to render this section's header and caption
 * with **nothing under it** — no rows, no "off" state, no statement that
 * components ship whole. That empty state IS the bug. An admin edited district
 * percentages believing they applied everywhere; two of three campuses were
 * off, so those campuses shipped every component at 100% — including 1,526
 * milk cartons that were then removed by hand, one line at a time. Nothing in
 * the UI said so. Every state this card can be in now says what it does, and
 * links to the district view so a campus admin can see whether the other
 * campuses agree with this one.
 *
 * ## Inherited vs overridden
 *
 * Every cell states whether its value is INHERITED from the district or
 * OVERRIDDEN here, and an override is clearable back to inherited.
 *
 * ⚠ The load-bearing invariant: **an inherited value that is merely being
 * DISPLAYED is never persisted.** Inherited numbers render as input
 * PLACEHOLDERS, never as values, and only cells the user actually typed are
 * written. Round-tripping the district's 50% into a stored campus row detaches
 * that campus permanently and a later district edit silently stops reaching it
 * — a strictly worse version of the bug above.
 *
 * "Component split": decomposable bundle rows on the order sheet default to a
 * % split per (mealtime × component kind × order column). The card renders ONE
 * sub-card per mealtime the school serves (summary line + Edit); Edit opens a
 * dialog containing the split grid scoped to that mealtime. Every edit writes a
 * MEALTIME-SCOPED, LOCATION-tier `ComponentDeliveryRate` / `MilkShareRate` row
 * — legacy mealtime-NULL rows are never mutated here; they remain the fallback
 * tier.
 * The old "Pre-fill meal selection" toggle is gone (Sheila, 2026-08-03 —
 * pre-fill and auto-order side by side were two names for one idea). Its
 * replacement, the campus's auto-order Y/N switch, lives in the Auto-orders
 * card on the settings page rather than here: this card only renders for
 * OVS campuses, and a Straight Serve campus must still be able to turn
 * auto-order on. The `prefillMealSelectionEnabled` column and its
 * order-history seeding survive untouched; only the toggle is gone.
 *
 * ## v6: every cell is editable, and nothing saves until Save (2026-08-26)
 *
 * Sheila: *"OVS is not allowing them to save/update number… These fields are
 * all uneditable… They should all be editable… And need save button."*
 *
 * An inherited cell used to render as an EMPTY input with the inherited number
 * as a placeholder. It looks filled and behaves as if it were read-only:
 * Backspace does nothing, arrow keys do nothing, and typing one digit replaces
 * the whole number. Combined with a 600 ms debounce autosave that died with
 * the input when the dialog closed, the surface read as "won't let me save".
 *
 * Now every cell holds its EFFECTIVE number as a real value, edits stage into
 * a `SplitEditor`, and the dialog's Save button writes them. The
 * never-persist-a-displayed-inherited-value invariant moved into
 * `draftActionFor` (`split-draft.ts`), which writes a cell only when the typed
 * number DIFFERS from what the cell already resolves to.
 *
 * Main entrée is pinned at 100% — a meal always ships one
 * main per meal ordered. Rows show only `SPLIT_COMPONENT_KINDS` — hidden
 * kinds (SIDE, SUPPLEMENT) implicitly ship at 100%.
 *
 * Cell display resolution per (kind × column) inside a mealtime's grid runs
 * the four mealtime/column tiers once per CASCADE SCOPE and overlays the
 * scopes district → campus:
 *   (mealtime, column) → (mealtime, wide) → (legacy, column) → (legacy, wide)
 * per scope, then ORGANIZATION → LOCATION → 100% / even milk split.
 * Scope is the OUTERMOST axis — a campus row beats a district row even when
 * the district row is more specific on the mealtime axis. A cell renders muted
 * until THIS mealtime at THIS campus owns the cell's exact row.
 *
 * Rate changes do NOT reach orders that have already been placed. The card
 * says so where the user edits, because the alternative is an admin assuming a
 * correction propagated backwards.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ordo/ui/alert-dialog'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Switch } from '@ordo/ui/switch'
import { CalendarDays, ChevronDown, ChevronRight, Pencil, Percent, Users } from 'lucide-react'
import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  type CampusSplitOverrideLayer,
  clearComponentDeliveryRate,
  clearMilkShareSet,
  type ComponentKind,
  componentKindLabel,
  type ComponentSplitStatus,
  type LocationComponentDeliveryRate,
  type LocationMilkShareRate,
  type MilkProfile,
  type MilkSku,
  SCHOOL_WEEKDAYS,
  SPLIT_COMPONENT_KINDS,
  upsertComponentDeliveryRate,
  useClearCampusSplitOverridesMutation,
  useComponentDeliveryRatesQuery,
  useMilkSharesQuery,
  useOrderPreferencesQuery,
  useUpdateOrderPreferencesMutation,
  useWeekdayRulesQuery,
  type Weekday,
  WEEKDAY_LABELS,
} from '@/api/location-settings'
import type { AdminSchool } from '@/api/school-orders'
import { type Mealtime, MEALTIME_LABELS, sortMealtimes } from '@/lib/mealtime'
import { formatMealtimeSplitSummary } from '@/lib/school-orders/breakdown'
import { buildOrderColumns, type OrderColumn, WHOLE_SCHOOL_COLUMN_ID } from '@/lib/school-orders/columns'
import { rateRowColumnKey } from '@/lib/school-orders/milk'
import {
  groupOverrideSummary,
  groupRateRowsFor,
  type GroupRowScope,
  groupShareSetsFor,
  hasGroupColumns,
  splitEditorColumns,
  supersededGroupScopes,
} from '@/lib/school-orders/split-columns'
import { effectiveCellBps } from '@/lib/school-orders/split-draft'
import {
  clearSplitOptOutMode,
  hasLegacySplitOptOut,
  resolveRateCell,
  type SplitCell,
} from '@/lib/school-orders/split-provenance'

// The milk-share editing pieces (the debounced % cell, the provenance chip,
// and the nested milk rows) are SHARED with the Straight Serve "Milk split"
// settings card — extracted to `milk-share-grid.tsx` so both surfaces write
// the same `MilkShareRate` rows through the same editor.
import { CardTitleRow } from './campus-scope'
import {
  columnCellKey,
  columnScope,
  milkCellKey,
  MilkShareRows,
  rateCellKey,
  SplitPercentInput,
  type SplitWriteScope,
} from './milk-share-grid'
import { SplitEditDialog } from './split-edit-dialog'
import { type SplitEditor, useSplitEditor } from './split-editor'

export interface PrefillSettingsCardProps {
  school: AdminSchool
  /**
   * The section's campus picker (`CampusScope`), rendered in this card's
   * header. Null/absent for a single-campus org — then the header is exactly
   * what it was before per-card scoping. It rides EVERY render branch below,
   * so the admin can never be stranded on one campus by a pending query.
   */
  scopeControl?: ReactNode
}

export function PrefillSettingsCard({ school, scopeControl }: PrefillSettingsCardProps) {
  const locationId = school.id
  const columns = useMemo(() => buildOrderColumns(school), [school])
  const summaryColumns = useMemo(
    () =>
      columns.map((column) => ({
        key: rateRowColumnKey(columnScope(column)),
        foldedColumnIds: column.coveredLocationGradeRangeIds,
      })),
    [columns],
  )
  const ratesQuery = useComponentDeliveryRatesQuery(locationId)
  const milkSharesQuery = useMilkSharesQuery(locationId)
  const preferencesQuery = useOrderPreferencesQuery(locationId)
  const weekdayRulesQuery = useWeekdayRulesQuery(locationId)
  const updatePreferences = useUpdateOrderPreferencesMutation()
  const navigate = useNavigate()
  const [editingMealtime, setEditingMealtime] = useState<Mealtime | null>(null)
  // Age-group exceptions are opt-in: the dialog opens on the one school value
  // unless this mealtime already stores age-group rows, which must be visible.
  const [showAgeGroups, setShowAgeGroups] = useState(false)
  // One editor per open dialog: cells stage their edits into it, the dialog's
  // Save button writes them, and closing with unsaved edits asks first.
  const editor = useSplitEditor(locationId)

  const rates = useMemo(() => ratesQuery.data?.rates ?? [], [ratesQuery.data])
  const shares = useMemo(() => milkSharesQuery.data?.shares ?? [], [milkSharesQuery.data])
  // The DISTRICT's own rows. Rendered as input PLACEHOLDERS only — never as
  // values. See `split-provenance.ts`: persisting a displayed inherited value
  // detaches this campus from the district permanently.
  const inheritedRates = useMemo(() => ratesQuery.data?.inheritedRates ?? [], [ratesQuery.data])
  const inheritedShares = useMemo(() => milkSharesQuery.data?.inheritedShares ?? [], [milkSharesQuery.data])
  const milkSkus = milkSharesQuery.data?.milkSkus ?? []
  // The school-level milk profile (district's chosen milk TYPES + noMilkMode)
  // — canonical source-of-truth context shown above the editable SKU rows.
  const milkProfile: MilkProfile = milkSharesQuery.data?.milkProfile ?? { milkOptionCodes: [], noMilkMode: false }

  // One card per served mealtime (provisioning truth off /my-schools), plus
  // a card for any mealtime that already has scoped rows but dropped out of
  // the served list — existing config must never become invisible. An
  // unprovisioned campus with no scoped rows falls back to a single Lunch
  // card so the split stays editable.
  const cardMealtimes = useMemo(() => {
    const served = school.servedMealtimes ?? []
    const stale = new Set<Mealtime>()
    for (const row of rates) {
      if (row.mealtime && !served.includes(row.mealtime)) {
        stale.add(row.mealtime)
      }
    }
    for (const row of shares) {
      if (row.mealtime && !served.includes(row.mealtime)) {
        stale.add(row.mealtime)
      }
    }
    const all = [...served, ...sortMealtimes(stale)]
    return all.length > 0 ? all : (['LUNCH'] as Mealtime[])
  }, [school.servedMealtimes, rates, shares])
  const servedSet = useMemo(() => new Set(school.servedMealtimes ?? []), [school.servedMealtimes])

  // ⚠ Every hook must sit ABOVE the loading/error early returns below —
  // `react-hooks/rules-of-hooks`. Placing these after them made the toggles'
  // `useState` conditional, so the hook order changed the moment a query
  // settled: React would have mismatched state slots and the toggles would have
  // read whatever the neighbouring hook held.
  //
  // Both toggles DERIVE their initial state from stored rows, so a campus that
  // already has overrides or weekday rules opens showing them. They are display
  // state only; resolution never reads them.
  // Campus-tier rows this campus owns. `rates` carries only `LOCATION`-scope
  // rows (the server filters `weekday: null`), so the weekday rules are counted
  // separately off their own summary — the two never double-count.
  const campusOverrideCount = rates.length + shares.length
  const weekdayRules = weekdayRulesQuery.data
  const weekdayRuleCount = weekdayRules?.totalCells ?? 0
  const weekdaysWithRules = (weekdayRules?.weekdays ?? []).map((w) => w.weekday)
  const [byCampus, setByCampus] = useState(false)
  const [byDay, setByDay] = useState(false)
  const clearOverrides = useClearCampusSplitOverridesMutation()
  // The queries resolve after the first render, so adopt their answer once
  // rather than stranding the toggles off for a campus that does have config.
  // Guarded by a ref so a later user toggle-off is never re-overridden.
  //
  // ⚠ Latch only once EVERY query has landed. Latching on `weekdayRules` alone
  // raced the rate/share queries: on a render where the weekday summary had
  // resolved and the rates had not, `campusOverrideCount` read 0, the ref
  // latched, and the campus was stranded showing "Configure by campus: off"
  // over six stored overrides that were still shipping (Hillsborough,
  // 2026-08-26).
  const derivedRef = useRef(false)
  useEffect(() => {
    if (
      derivedRef.current ||
      weekdayRules === undefined ||
      ratesQuery.data === undefined ||
      milkSharesQuery.data === undefined
    ) {
      return
    }
    derivedRef.current = true
    if (campusOverrideCount > 0) {
      setByCampus(true)
    }
    if (weekdayRules.totalCells > 0) {
      setByCampus(true)
      setByDay(true)
    }
  }, [weekdayRules, campusOverrideCount, ratesQuery.data, milkSharesQuery.data])

  /**
   * Turning a granularity toggle OFF now REMOVES that layer's stored rows.
   *
   * Sheila, 2026-08-26: *"When i turn off ovs settings can you make it just
   * remove all school values and revert to a single org level setting, but have
   * a warning message first"*. Before this the toggle only HID the rows — they
   * kept resolving, and the card had to admit as much in an amber line. A
   * display control whose label disagrees with what ships is the same defect as
   * the retired `prefillComponentSplitEnabled` switch.
   *
   * `AdvancedOptions` owns the are-you-sure; this only runs after the user has
   * confirmed against an exact count.
   */
  async function clearLayer(layer: CampusSplitOverrideLayer): Promise<boolean> {
    try {
      const cleared = await clearOverrides.mutateAsync({ locationId, layer })
      const cells = cleared.rateCells + cleared.milkCells
      toast.success(
        layer === 'CAMPUS'
          ? `${school.name} now follows the district${cells > 0 ? ` — ${cells} override${cells === 1 ? '' : 's'} removed` : ''}`
          : `${cells} weekday rule${cells === 1 ? '' : 's'} removed`,
      )
      return true
    } catch (error) {
      toast.error(
        error instanceof Error ? `Couldn't remove the overrides: ${error.message}` : "Couldn't remove the overrides",
      )
      return false
    }
  }

  // ONE header title row, reused by every render branch below — the campus
  // picker must be present in all of them (see `campus-scope.tsx`).
  const titleRow = (
    <CardTitleRow scopeControl={scopeControl}>
      <CardTitle className="flex items-center gap-2 text-base">
        <Percent className="h-4 w-4" />
        Pre-fill settings
      </CardTitle>
    </CardTitleRow>
  )

  if (ratesQuery.isPending || milkSharesQuery.isPending || preferencesQuery.isPending || weekdayRulesQuery.isPending) {
    return (
      <Card>
        <CardHeader className="pb-3">{titleRow}</CardHeader>
        <CardContent className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }
  const loadError = ratesQuery.error ?? preferencesQuery.error
  if (loadError || !preferencesQuery.data) {
    return (
      <Card>
        {/* The header exists in this branch ONLY to keep the campus picker
            reachable when the load fails — a single-campus org keeps the
            original bare error card. */}
        {scopeControl ? <CardHeader className="pb-3">{titleRow}</CardHeader> : null}
        <CardContent className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
          Couldn't load pre-fill settings. {loadError?.message}
        </CardContent>
      </Card>
    )
  }
  const preferences = preferencesQuery.data

  const split = preferences.componentSplit

  return (
    <Card>
      <CardHeader className="pb-3">{titleRow}</CardHeader>
      <CardContent className="flex flex-col gap-6">
        <section>
          <div>
            <h3 className="text-sm font-medium">Component split</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              When ordering bulk meals by component, ship components in this split. Offer Versus Serve always ships a
              split — to send a component whole, set it to 100%.
            </p>
          </div>

          <SplitStateNotice
            split={split}
            schoolName={school.name}
            onClearOptOut={() =>
              updatePreferences.mutate({ locationId, componentSplitMode: clearSplitOptOutMode(split) })
            }
            clearPending={updatePreferences.isPending}
          />

          {/* The percentage editor renders unconditionally. There is no
                off state to hide it behind any more, and a campus carrying a
                legacy opt-out needs it MOST: setting these to 100% is how it
                says "ship everything whole" under the new rule. */}
          <div className="mt-3 flex flex-col gap-2">
            {cardMealtimes.map((m) => (
              <div key={m} className="flex items-center justify-between gap-4 rounded-lg border p-4">
                <div className="min-w-0">
                  <h4 className="text-sm font-medium">
                    {MEALTIME_LABELS[m]}
                    {servedSet.has(m) || cardMealtimes.length === 1 ? null : (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">(not served)</span>
                    )}
                  </h4>
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {formatMealtimeSplitSummary(rates, m, { inheritedRates, columns: summaryColumns })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAgeGroups(groupOverrideSummary(rates, shares, m).count > 0)
                    setEditingMealtime(m)
                  }}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              </div>
            ))}
          </div>

          <AdvancedOptions
            schoolName={school.name}
            campusOverrideCount={campusOverrideCount}
            weekdayRuleCount={weekdayRuleCount}
            weekdaysWithRules={weekdaysWithRules}
            byCampus={byCampus}
            onByCampusChange={setByCampus}
            byDay={byDay}
            onByDayChange={setByDay}
            onClearLayer={clearLayer}
            clearPending={clearOverrides.isPending}
          />

          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
            onClick={() => navigate('/settings/my-school/district-split')}>
            <Users className="h-3.5 w-3.5" />
            Compare every campus in {school.organization.name}
          </button>

          <p className="mt-2 text-xs text-muted-foreground">
            Changing these percentages affects orders placed from now on. Orders already placed keep the split they were
            placed with.
          </p>
        </section>
      </CardContent>

      <SplitEditDialog
        open={editingMealtime !== null}
        onClose={() => setEditingMealtime(null)}
        editor={editor}
        title={`Pre-fill component split — ${editingMealtime ? MEALTIME_LABELS[editingMealtime] : ''}`}
        description={
          <>
            The share of each meal part {school.name} ships at{' '}
            {editingMealtime ? MEALTIME_LABELS[editingMealtime].toLowerCase() : ''}. A cell marked
            &ldquo;District&rdquo; follows the district — type a different number to override it for this school, and
            clear the box to go back to the district. Nothing is written until you press{' '}
            <span className="font-medium text-foreground">Save changes</span>, and saving applies to orders placed from
            now on.
          </>
        }>
        {editingMealtime && hasGroupColumns(columns) ? (
          <AgeGroupExceptions
            locationId={locationId}
            mealtime={editingMealtime}
            rates={rates}
            shares={shares}
            open={showAgeGroups}
            onOpenChange={setShowAgeGroups}
            editor={editor}
          />
        ) : null}
        {editingMealtime ? (
          <SplitGrid
            locationId={locationId}
            columns={splitEditorColumns(columns, { showGroups: showAgeGroups })}
            mealtime={editingMealtime}
            weekdays={byDay ? SCHOOL_WEEKDAYS : []}
            rates={rates}
            shares={shares}
            inheritedRates={inheritedRates}
            inheritedShares={inheritedShares}
            milkSkus={milkSkus}
            milkProfile={milkProfile}
            editor={editor}
          />
        ) : null}
      </SplitEditDialog>
    </Card>
  )
}

/**
 * Advanced Options — the granularity toggles.
 *
 * Sheila, 2026-08-20: *"Maybe we offer it as a toggle though in advanced
 * settings? Like Configure by Campus, by day."*
 *
 * ⚠ These remain DISPLAY/AUTHORING affordances, not gates. Resolution is driven
 * entirely by which rows exist (`packages/schema/src/managers/rate-cascade.ts`);
 * nothing here is consulted when deciding how much food ships. That separation
 * is deliberate and load-bearing: a stored "configure by day" boolean would be
 * a second switch that has to agree with the rows, which is precisely the
 * `prefillComponentSplitEnabled` mistake this work retires.
 *
 * Initial state is DERIVED from whether rows exist at that scope, so opening a
 * campus that already has weekday rules shows them instead of hiding them
 * behind an off switch.
 *
 * ## Turning a toggle off REMOVES that layer (2026-08-26)
 *
 * Sheila: *"When i turn off ovs settings can you make it just remove all school
 * values and revert to a single org level setting, but have a warning message
 * first"*.
 *
 * Before this, off only HID the layer, and the card had to admit in amber that
 * "6 campus overrides are stored and still in effect. Turning this off only
 * hides them." That is the display control disagreeing with what ships — the
 * same class of defect as the retired split switch, arrived at from the other
 * direction. Off now means the rows are gone and the campus follows the
 * district, which is exactly what the label already claimed.
 *
 * The destructive half is gated on an are-you-sure that names the exact count,
 * because it cannot be undone from the UI: nothing re-attaches the rows, and
 * getting them back means re-typing every percentage. Turning the toggle back
 * ON afterwards gives an empty campus layer, not the old numbers.
 *
 * Turning a toggle ON is never destructive and never asks.
 */
function AdvancedOptions({
  schoolName,
  campusOverrideCount,
  weekdayRuleCount,
  weekdaysWithRules,
  byCampus,
  onByCampusChange,
  byDay,
  onByDayChange,
  onClearLayer,
  clearPending,
}: {
  schoolName: string
  campusOverrideCount: number
  weekdayRuleCount: number
  weekdaysWithRules: Weekday[]
  byCampus: boolean
  onByCampusChange: (next: boolean) => void
  byDay: boolean
  onByDayChange: (next: boolean) => void
  /** Removes the layer's stored rows; resolves false when the write failed. */
  onClearLayer: (layer: CampusSplitOverrideLayer) => Promise<boolean>
  clearPending: boolean
}) {
  const [open, setOpen] = useState(
    // Open by default when there is something in here to see, so existing
    // configuration is never hidden behind a collapsed section.
    () => campusOverrideCount > 0 || weekdayRuleCount > 0,
  )
  const [confirming, setConfirming] = useState<CampusSplitOverrideLayer | null>(null)

  // Turning "by campus" off takes the weekday rules with it — a weekday rule
  // belongs to a campus, so leaving them behind would keep the campus
  // overriding the district through a layer the UI has just hidden.
  const campusTotal = campusOverrideCount + weekdayRuleCount

  function requestByCampus(next: boolean) {
    if (next || campusTotal === 0) {
      onByCampusChange(next)
      if (!next) {
        onByDayChange(false)
      }
      return
    }
    setConfirming('CAMPUS')
  }

  function requestByDay(next: boolean) {
    if (next || weekdayRuleCount === 0) {
      onByDayChange(next)
      return
    }
    setConfirming('WEEKDAY')
  }

  async function confirmClear() {
    const layer = confirming
    if (!layer) {
      return
    }
    const ok = await onClearLayer(layer)
    setConfirming(null)
    if (!ok) {
      return // the rows are still there — leave the toggle where it was
    }
    if (layer === 'CAMPUS') {
      onByCampusChange(false)
      onByDayChange(false)
      return
    }
    onByDayChange(false)
  }

  const confirmCount = confirming === 'CAMPUS' ? campusTotal : weekdayRuleCount
  return (
    <section className="mt-4 rounded-lg border">
      <button
        type="button"
        className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Advanced options
        {!open && (campusOverrideCount > 0 || weekdayRuleCount > 0) ? (
          <span className="font-normal text-muted-foreground">
            {[
              campusOverrideCount > 0
                ? `${campusOverrideCount} campus override${campusOverrideCount === 1 ? '' : 's'}`
                : null,
              weekdayRuleCount > 0 ? `${weekdayRuleCount} weekday rule${weekdayRuleCount === 1 ? '' : 's'}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="flex flex-col gap-4 border-t p-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h4 className="text-sm font-medium">Configure by campus</h4>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {byCampus
                  ? 'This campus can set its own percentages, overriding the district.'
                  : 'The district percentages apply here. Turn this on to set campus-specific values.'}
              </p>
              {byCampus && campusTotal > 0 ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {campusTotal} override{campusTotal === 1 ? '' : 's'} set here. Turning this off removes{' '}
                  {campusTotal === 1 ? 'it' : 'them'} and hands the campus back to the district.
                </p>
              ) : null}
            </div>
            <Switch
              checked={byCampus}
              disabled={clearPending}
              onCheckedChange={requestByCampus}
              aria-label="Configure by campus"
            />
          </div>

          {/* Subordinate on purpose: the owner is clear this is the extra
              complicated layer for complicated schools, not something every
              district should meet. Indented, and only meaningful alongside
              by-campus — a weekday rule is always scoped to one campus. */}
          <div className="ml-6 border-l pl-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h4 className="flex items-center gap-1.5 text-sm font-medium">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Configure by day
                </h4>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {byDay
                    ? 'Set a recurring rule for one weekday, e.g. Fridays run a higher vegetable share.'
                    : 'Campus percentages apply to every service day.'}
                </p>
                {!byCampus ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Turn on “Configure by campus” first — a weekday rule belongs to a campus.
                  </p>
                ) : null}
                {byDay && weekdayRuleCount > 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {weekdayRuleCount} rule{weekdayRuleCount === 1 ? '' : 's'} set on{' '}
                    {weekdaysWithRules.map((w) => WEEKDAY_LABELS[w]).join(', ')}. Turning this off removes{' '}
                    {weekdayRuleCount === 1 ? 'it' : 'them'}.
                  </p>
                ) : null}
              </div>
              <Switch
                checked={byDay}
                disabled={!byCampus || clearPending}
                onCheckedChange={requestByDay}
                aria-label="Configure by day"
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* The warning. Named counts, named consequence, and the fact that it
          cannot be undone — a bare "Are you sure?" over a destructive write
          the user cannot reverse is not a warning, it is a speed bump. */}
      <AlertDialog open={confirming !== null} onOpenChange={(next) => (next ? null : setConfirming(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'CAMPUS'
                ? `Remove ${schoolName}'s percentages?`
                : `Remove ${schoolName}'s weekday rules?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'CAMPUS' ? (
                <>
                  This deletes the {confirmCount} percentage override{confirmCount === 1 ? '' : 's'} set at {schoolName}
                  {weekdayRuleCount > 0
                    ? `, its ${weekdayRuleCount} weekday rule${weekdayRuleCount === 1 ? '' : 's'} included`
                    : ''}
                  , and hands the campus back to the single district-wide setting. It can&rsquo;t be undone — turning
                  this back on gives you an empty campus, not these numbers. Orders already placed keep the split they
                  were placed with.
                </>
              ) : (
                <>
                  This deletes the {confirmCount} weekday rule{confirmCount === 1 ? '' : 's'} set at {schoolName}. Every
                  service day goes back to the campus percentages. It can&rsquo;t be undone. Orders already placed keep
                  the split they were placed with.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearPending}>Keep them</AlertDialogCancel>
            <AlertDialogAction
              disabled={clearPending}
              onClick={(event) => {
                // Radix closes the dialog on action click; the write is async
                // and its failure has to be able to leave the toggle alone.
                event.preventDefault()
                void confirmClear()
              }}>
              {clearPending ? 'Removing…' : `Remove ${confirmCount}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

/**
 * States, in words, what the split is doing at this campus and WHERE that came
 * from. This banner is the direct answer to *"in the app it doesn't show me
 * that prefill isn't on any of the other locations, the ui/ux is confusing."*
 *
 * Since 2026-08-26 this card renders for OVS campuses only and OVS campuses
 * cannot be switched off — so the only way to be off here is a LEGACY stored
 * opt-out, written before the switch was removed. That state gets an
 * explanation and a one-way exit, never a toggle:
 *
 *   - legacy opt-out (this campus or the district) → say that orders still
 *     ship 100% today, say the rule ("set the percentages to 100%"), and offer
 *     the one button that clears it
 *   - on, overriding a legacy district opt-out     → worth saying out loud,
 *     because the district view shows this campus as the odd one out
 *   - on, inheriting                               → the ordinary case; quiet
 *
 * ⚠ The notice must NOT claim the split is already applying while a `DISABLED`
 * row is still stored. The server keeps honouring it (resolution is untouched
 * on purpose), and a card that says "on" over an order sheet that ships 100% is
 * the same lie the retired switch told from the other direction.
 */
function SplitStateNotice({
  split,
  schoolName,
  onClearOptOut,
  clearPending,
}: {
  split: ComponentSplitStatus
  schoolName: string
  onClearOptOut: () => void
  clearPending: boolean
}) {
  if (hasLegacySplitOptOut(split)) {
    const atCampus = split.source === 'LOCATION'
    return (
      <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
        <p className="font-medium text-amber-700 dark:text-amber-500">
          {atCampus ? `An old opt-out is still set at ${schoolName}` : 'An old district-wide opt-out is still set'}
        </p>
        <p className="mt-0.5 text-muted-foreground">
          Offer Versus Serve always runs a component split, so this can no longer be switched off. Until it is cleared,
          bulk component orders{' '}
          {atCampus
            ? 'here still ship 100% of every component'
            : 'at every campus in the district still ship 100% of every component'}
          . To keep shipping everything whole, clear it and set the percentages below to 100%.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={clearPending}
          onClick={onClearOptOut}>
          {atCampus ? 'Clear the old opt-out' : `Clear it for ${schoolName}`}
        </Button>
        {atCampus ? null : (
          <p className="mt-2 text-xs text-muted-foreground">
            This clears it for {schoolName} only. Clear it for every campus from the district view below.
          </p>
        )}
      </div>
    )
  }
  if (split.locationMode === 'ENABLED' && split.organizationMode === 'DISABLED') {
    return (
      <div className="mt-3 rounded-md border border-border bg-muted/40 p-3 text-sm">
        <p className="font-medium">Splitting at {schoolName} only</p>
        <p className="mt-0.5 text-muted-foreground">
          The district still carries an old component-split opt-out; this campus overrides it.
        </p>
      </div>
    )
  }
  return null
}

/**
 * The kind × order-column % grid for ONE mealtime — the body of the Edit
 * dialog. Cells display the 4-tier resolved value and write
 * mealtime-scoped rows on edit.
 */
/**
 * The component-split grid for ONE mealtime.
 *
 * Exported so the auto-order wizard's OVS step renders the SAME editor.
 * Sheila, 2026-08-03: *"No duplicate, use single % setting that impacts both
 * auto order and manual order."* Its cells own the
 * `useUpsertComponentDeliveryRate` / `useUpsertMilkShareRate` mutations, so
 * an edit anywhere writes the campus's `LocationComponentDeliveryRate` rows
 * — the same rows the order sheet resolves and the auto-order fill path
 * reads live. Copying this grid into the wizard would have created exactly
 * the second setting that must not exist.
 */
/**
 * The opt-in age-group exceptions above a school's split grid.
 *
 * A school's override is ONE value per meal part (`split-columns.ts`). The
 * age-group columns only appear when the admin opens them, or when this
 * mealtime already stores age-group rows — those outrank the school value for
 * their students, so hiding them would make the school column a lie.
 */
function AgeGroupExceptions({
  locationId,
  mealtime,
  rates,
  shares,
  open,
  onOpenChange,
  editor,
}: {
  locationId: string
  mealtime: Mealtime
  rates: LocationComponentDeliveryRate[]
  shares: LocationMilkShareRate[]
  open: boolean
  onOpenChange: (open: boolean) => void
  editor: SplitEditor
}) {
  const summary = groupOverrideSummary(rates, shares, mealtime)
  const clearKey = `age-groups::clear::${mealtime}`
  const clearStaged = editor.staged[clearKey]?.run != null
  const parts = [
    ...summary.componentKinds.map((kind) => componentKindLabel(kind as ComponentKind).toLowerCase()),
    ...(summary.milkShares ? ['the milk split'] : []),
  ]

  function stageClearAll() {
    editor.stage(clearKey, {
      raw: '',
      invalid: false,
      run: async () => {
        for (const kind of summary.componentKinds) {
          for (const group of groupRateRowsFor(rates, kind, mealtime)) {
            await clearComponentDeliveryRate({ locationId, componentKind: kind as ComponentKind, mealtime, ...group })
          }
        }
        for (const group of groupShareSetsFor(shares, mealtime)) {
          await clearMilkShareSet({ locationId, mealtime, ...group })
        }
      },
    })
  }

  return (
    <div className="mb-3 flex flex-col gap-2">
      {summary.count > 0 ? (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          {clearStaged ? (
            <>Saving removes the age-group values, so every student here gets the school value.</>
          ) : (
            <>
              Age groups here have their own {parts.join(', ')} — those students get their age group&rsquo;s number, not
              the school&rsquo;s. Changing a school value replaces its age-group values.
              <Button type="button" variant="link" size="sm" className="h-auto px-1 py-0" onClick={stageClearAll}>
                Use one school value for everyone
              </Button>
            </>
          )}
        </div>
      ) : null}
      <button
        type="button"
        className="inline-flex items-center gap-1.5 self-start text-sm text-primary underline-offset-4 hover:underline"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}>
        <Users className="h-3.5 w-3.5" />
        {open ? 'Hide age groups' : 'Different split for an age group'}
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open ? (
        <p className="text-xs text-muted-foreground">
          Only change an age group that&rsquo;s served differently. An age group you leave alone follows the school
          value.
        </p>
      ) : null}
    </div>
  )
}

export function SplitGrid({
  locationId,
  columns,
  mealtime,
  rates,
  shares,
  inheritedRates = [],
  inheritedShares = [],
  milkSkus,
  milkProfile,
  weekdays = [],
  editor,
  writeScope,
}: {
  locationId: string
  columns: OrderColumn[]
  mealtime: Mealtime
  /** Stages every cell's edit; the host's Save button performs the writes. */
  editor: SplitEditor
  /** Which cascade tier this grid writes. Omitted = the campus's own tier. */
  writeScope?: SplitWriteScope
  /**
   * Weekdays to expose a recurring-rule column for. EMPTY (the default) means
   * the "Configure by day" toggle is off, so the grid shows only the campus
   * values — which is also exactly what the auto-order wizard wants.
   */
  weekdays?: ReadonlyArray<Weekday>
  rates: LocationComponentDeliveryRate[]
  shares: LocationMilkShareRate[]
  /** The DISTRICT's rows — placeholders only, never values. */
  inheritedRates?: LocationComponentDeliveryRate[]
  inheritedShares?: LocationMilkShareRate[]
  milkSkus: MilkSku[]
  milkProfile: MilkProfile
}) {
  // The column key `split-provenance` uses for a row: the whole-school column
  // edits the wide (null-tuple) rows, so its key is `default`.
  const columnKeyOf = (row: { groupDimension: string | null; groupValue: string | null }) => rateRowColumnKey(row)

  // A school value replaces that part's age-group rows (`split-columns.ts`).
  // Only at the campus tier: the district and weekday grids write rows no
  // age-group row sits above in the same tier.
  const supersedesGroups = !writeScope?.scopeType || writeScope.scopeType === 'LOCATION'
  // Group row key → the column id its staged cells are keyed by, so a save can
  // tell which age groups the user is editing as exceptions right now.
  const columnIdByGroupKey = new Map(
    columns.flatMap((col) => {
      const key = rateRowColumnKey(columnScope(col))
      return key === null ? [] : [[key, col.columnId] as const]
    }),
  )
  function stagedGroupKeys(isStaged: (columnId: string) => boolean): Set<string> {
    const keys = new Set<string>()
    for (const [groupKey, columnId] of columnIdByGroupKey) {
      if (isStaged(columnId)) {
        keys.add(groupKey)
      }
    }
    return keys
  }
  const hasRun = (cellKey: string) => editor.stagedRef.current[cellKey]?.run != null
  function supersededRateGroups(kind: ComponentKind): GroupRowScope[] {
    return supersededGroupScopes(
      groupRateRowsFor(rates, kind, mealtime),
      stagedGroupKeys((columnId) => hasRun(rateCellKey(mealtime, columnId, kind))),
    )
  }
  function supersededMilkGroups(): GroupRowScope[] {
    return supersededGroupScopes(
      groupShareSetsFor(shares, mealtime),
      stagedGroupKeys((columnId) => milkSkus.some((sku) => hasRun(milkCellKey(mealtime, columnId, sku.id)))),
    )
  }

  /**
   * Resolve one (kind × column) cell across BOTH tiers, with provenance.
   *
   * `cell.value === null` means the number shown is inherited, and the input
   * renders it as a PLACEHOLDER. That is the mechanism that keeps a displayed
   * district value from being persisted as a campus override — the single most
   * important correctness rule in this editor.
   */
  function rateCellFor(kind: ComponentKind, column: OrderColumn): SplitCell {
    return resolveRateCell({
      ownRows: rates,
      inheritedRows: inheritedRates,
      componentKind: kind,
      mealtime,
      columnKey: columnCellKey(column),
      columnKeyOf,
    })
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-4 font-medium">Meal part</th>
            {columns.map((col) => (
              <th key={col.columnId} className="whitespace-nowrap py-2 pl-4 text-right font-medium">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SPLIT_COMPONENT_KINDS.map((kind) => (
            <Fragment key={kind}>
              <tr className="border-b last:border-b-0">
                <td className="py-2 pr-4">{componentKindLabel(kind)}</td>
                {columns.map((col) => (
                  <td key={col.columnId} className="py-2 pl-4 text-right">
                    {kind === 'MAIN_ENTREE' ? (
                      <span className="text-xs text-muted-foreground">100% · one per meal</span>
                    ) : (
                      <SplitPercentCell
                        locationId={locationId}
                        componentKind={kind}
                        column={col}
                        mealtime={mealtime}
                        cell={rateCellFor(kind, col)}
                        editor={editor}
                        writeScope={writeScope}
                        supersede={
                          supersedesGroups && col.columnId === WHOLE_SCHOOL_COLUMN_ID
                            ? () => supersededRateGroups(kind)
                            : undefined
                        }
                      />
                    )}
                  </td>
                ))}
              </tr>
              {/* Recurring weekday rules for this kind, revealed only by
                  "Configure by day". Deliberately subordinate: this is the
                  extra complicated layer for complicated schools, and putting
                  it in front of every district is how the original confusion
                  happened. */}
              {weekdays.length > 0 && kind !== 'MAIN_ENTREE' ? (
                <WeekdayRuleCells
                  locationId={locationId}
                  componentKind={kind}
                  mealtime={mealtime}
                  weekdays={weekdays}
                  columnSpan={columns.length}
                  campusCell={rateCellFor(kind, columns[0])}
                  rows={rates}
                  columnKeyOf={columnKeyOf}
                  editor={editor}
                />
              ) : null}
              {/* Nested milk shares — the school milk profile (source of
                  truth for which milk TYPES this school serves) followed
                  by one indented row per linked milk SKU, each a % OF THE
                  MILK TOTAL (not the meal count). Shown when the school
                  links milk SKUs OR has a configured milk profile. */}
              {kind === 'BEVERAGE' &&
              (milkSkus.length > 0 || milkProfile.milkOptionCodes.length > 0 || milkProfile.noMilkMode) ? (
                <MilkShareRows
                  locationId={locationId}
                  mealtime={mealtime}
                  milkSkus={milkSkus}
                  milkProfile={milkProfile}
                  shares={shares}
                  inheritedShares={inheritedShares}
                  columnKeyOf={columnKeyOf}
                  columns={columns}
                  editor={editor}
                  writeScope={writeScope}
                  supersedeGroupSets={supersedesGroups ? supersededMilkGroups : undefined}
                />
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * One % cell of the split grid, scoped to (mealtime × order column).
 * Displays the 4-tier resolved value; every edit writes a
 * MEALTIME-SCOPED row for this cell's column (never mutates the legacy
 * mealtime-NULL rows from here).
 */
function SplitPercentCell({
  locationId,
  componentKind,
  column,
  mealtime,
  cell,
  editor,
  writeScope,
  supersede,
}: {
  locationId: string
  componentKind: ComponentKind
  column: OrderColumn
  mealtime: Mealtime
  cell: SplitCell
  editor: SplitEditor
  writeScope?: SplitWriteScope
  /**
   * The school column only: the age-group rows this school value replaces,
   * read at SAVE time so exceptions typed in the same save survive.
   */
  supersede?: () => GroupRowScope[]
}) {
  const scope = { locationId, componentKind, mealtime, ...columnScope(column), ...writeScope }
  return (
    <SplitPercentInput
      cellKey={rateCellKey(mealtime, column.columnId, componentKind)}
      cell={cell}
      editor={editor}
      makeRun={(action) => async () => {
        if (action.kind === 'clear') {
          // Archives this tier's row so the cell inherits again. A cell with
          // no row of its own is left alone server-side.
          await clearComponentDeliveryRate(scope)
        } else {
          await upsertComponentDeliveryRate({ ...scope, deliveryRateBps: action.bps })
        }
        // One stored value per part: an age-group row would outrank the school
        // row for its students, so the school save would not reach them.
        for (const group of supersede?.() ?? []) {
          await clearComponentDeliveryRate({ locationId, componentKind, mealtime, ...group })
        }
      }}
      ariaLabel={`${componentKindLabel(componentKind)} percent for ${column.label} at ${MEALTIME_LABELS[mealtime]}`}
    />
  )
}

/**
 * One kind's recurring weekday rules — a compact `Mon Tue Wed Thu Fri` strip
 * under the kind's campus row.
 *
 * Each cell is a `LOCATION_WEEKDAY`-scoped override for that kind on that
 * weekday. Empty means the day inherits the campus value; the campus value is
 * the placeholder, so a displayed inherited number can never be persisted —
 * the same invariant that governs every other tier here.
 *
 * Scoped to the campus-wide (first) column on purpose. A weekday × order-column
 * cell is expressible in the schema, but authoring it here would be a five-fold
 * grid inside an already-nested dialog, and nobody has asked for it. If it is
 * ever needed, the writer and resolver already handle the tuple.
 */
function WeekdayRuleCells({
  locationId,
  componentKind,
  mealtime,
  weekdays,
  columnSpan,
  campusCell,
  rows,
  columnKeyOf,
  editor,
}: {
  locationId: string
  componentKind: ComponentKind
  mealtime: Mealtime
  weekdays: ReadonlyArray<Weekday>
  columnSpan: number
  campusCell: SplitCell
  rows: LocationComponentDeliveryRate[]
  columnKeyOf: (row: { groupDimension: string | null; groupValue: string | null }) => string | null
  editor: SplitEditor
}) {
  /** The value this weekday itself stores for the kind, if any. */
  function ownBpsFor(weekday: Weekday): number | null {
    const row = rows.find(
      (candidate) =>
        candidate.scopeType === 'LOCATION_WEEKDAY' &&
        candidate.weekday === weekday &&
        candidate.componentKind === componentKind &&
        candidate.mealtime === mealtime &&
        columnKeyOf(candidate) === null,
    )
    return row ? row.deliveryRateBps : null
  }

  // What a day resolves to when it has no rule of its own: the campus value,
  // else whatever the campus itself inherits, else the platform 100%.
  const inheritedBps = effectiveCellBps(campusCell)

  return (
    <tr className="border-b bg-muted/20 last:border-b-0">
      <td colSpan={columnSpan + 1} className="py-2 pl-6 pr-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            Every
          </span>
          {weekdays.map((weekday) => {
            const own = ownBpsFor(weekday)
            const scope = { locationId, componentKind, mealtime, scopeType: 'LOCATION_WEEKDAY' as const, weekday }
            return (
              <label key={weekday} className="flex items-center gap-1 text-xs">
                <span className={own === null ? 'text-muted-foreground' : 'font-medium'}>
                  {WEEKDAY_LABELS[weekday]}
                </span>
                <SplitPercentInput
                  cellKey={`rate::${mealtime}::weekday::${weekday}::${componentKind}`}
                  cell={{
                    value: own,
                    inheritedBps,
                    owned: own !== null,
                    source: own !== null ? 'LOCATION_WEEKDAY' : campusCell.source,
                  }}
                  editor={editor}
                  makeRun={(action) => async () => {
                    if (action.kind === 'clear') {
                      await clearComponentDeliveryRate(scope)
                      return
                    }
                    await upsertComponentDeliveryRate({ ...scope, deliveryRateBps: action.bps })
                  }}
                  ariaLabel={`${componentKindLabel(componentKind)} percent every ${WEEKDAY_LABELS[weekday]} at ${MEALTIME_LABELS[mealtime]}`}
                />
              </label>
            )
          })}
        </div>
      </td>
    </tr>
  )
}
